import axios from 'axios'
import Cookies from 'js-cookie'
import { handleUnGzipData } from '@/lib/api/use-get-data'
import * as Sentry from '@sentry/browser'

const CONTENT_TYPE_OF_COGNITO = 'application/x-www-form-urlencoded'
const AUTHORIZATION_BASIC_ID = `Basic ${process.env.VUE_APP_BASIC_ID}`

type KumasanAuthType = {
  _queryCode: string
  _redirectUri: string
  _clientId: string
  _updateIdToken: Function
}
type ObjectType = {
  [key: string]: string
}

let _intervalId = 0

export class KumasanAuth implements KumasanAuthType {
  _queryCode: string
  _redirectUri: string
  _clientId: string
  _updateIdToken: Function

  // コンストラクタ
  constructor() {
    let queryString = window.location.search
    const queryObject: ObjectType = {}
    if (queryString) {
      queryString = queryString.substring(1)
      const parameters = queryString.split('&')
      for (let i = 0; i < parameters.length; i++) {
        const element = parameters[i].split('=')
        const paramName = decodeURIComponent(element[0])
        const paramValue = decodeURIComponent(element[1])
        queryObject[paramName] = paramValue
      }
    }

    this._queryCode = queryObject.code
    this._updateIdToken = this.updateIdToken
    this._redirectUri = process.env.VUE_APP_REDIRECT_URL
    this._clientId = process.env.VUE_APP_CLIENT_ID

    clearInterval(_intervalId)
    _intervalId = setInterval(this.checkToken, 5000)
  }

  checkTokenClear() {
    clearInterval(_intervalId)
  }

  // cognitoのメアド、パスワードを使ってログイン
  // IdTokenとrefreshTokenをcookieに保存する
  // ※非同期処理
  async authenticate() {
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Basic ${process.env.VUE_APP_BASIC_ID}`,
    }
    // cookieにトークンがある場合は認証済みなのでスキップ
    if (this.getIdToken() != '') {
      location.pathname === '/auth' ? location.replace('/dashboard') : false

      return false // cookieが存在するので、自動でログインする（=本関数は終了する）
    }
    const params = new URLSearchParams()
    params.append('grant_type', 'authorization_code')
    params.append('redirect_uri', this._redirectUri)
    params.append('code', this._queryCode)
    params.append('client_id', this._clientId)
    return await axios
      .post(process.env.VUE_APP_COGNITO_URL + '/oauth2/token', params, {
        headers: headers,
      })
      .then(function(data) {
        const _idToken = data.data.id_token
        const _refreshToken = data.data.refresh_token
        const _accessToken = data.data.access_token

        const _refreshTokenExpire = new Date()
        _refreshTokenExpire.setTime(
          _refreshTokenExpire.getTime() + 60 * 60 * 1000
        )

        document.cookie =
          'kumasan_idToken=' +
          _idToken +
          '; domain=' +
          document.domain +
          '; path=/;'
        document.cookie =
          'kumasan_refreshToken=' +
          _refreshToken +
          ';domain=' +
          document.domain +
          ';path=/;'
        document.cookie =
          'kumasan_accessToken=' +
          _accessToken +
          '; domain=' +
          document.domain +
          '; path=/;'

        document.cookie =
          'refreshToken_expire=' +
          _refreshTokenExpire +
          ';domain=' +
          document.domain +
          ';path=/;'

        return
      })
      .catch(function(err) {
        alert('ログインしてください。')
        console.log('error----------------')
        document.cookie =
          'kumasan_idToken=; domain=' + document.domain + ';expires=0;path=/;'
        document.cookie =
          'kumasan_refreshToken=; domain=' +
          document.domain +
          ';expires=0;path=/;'
        document.cookie =
          'kumasan_accessToken=; domain=' +
          document.domain +
          ';expires=0;path=/;'
        console.error(err)
        Sentry.withScope(scope => {
          scope.setExtra('postData', params)
          Sentry.captureException(err)
        })
        location.replace('/login')
        throw err
      })
  }

  // acceccTokenからユーザー情報を取得する
  // ※非同期処理
  async getUser() {
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: 'Bearer ' + this.getAccessToken(),
    }
    const params = new URLSearchParams()
    params.append('client_id', this._clientId)
    return await axios
      .post(process.env.VUE_APP_COGNITO_URL + '/oauth2/userInfo', params, {
        headers: headers,
      })
      .then(function(data) {
        const res: ObjectType = {}
        res['mail'] = data.data.email
        res['user_id'] = data.data.username
        return res
      })
      .catch(function(err) {
        console.error(err)
        Sentry.withScope(scope => {
          scope.setExtra('postData', params)
          Sentry.captureException(err)
        })
        alert('ログインしてください。')
        document.cookie =
          'kumasan_idToken=; domain=' + document.domain + ';expires=0;path=/;'
        document.cookie =
          'kumasan_refreshToken=; domain=' +
          document.domain +
          ';expires=0;path=/;'
        document.cookie =
          'kumasan_accessToken=; domain=' +
          document.domain +
          ';expires=0;path=/;'
        location.replace('/login')
        throw err
      })
  }

  // refreshTokenからIdTokenを更新する
  // IdTokenは最大1hの有効期限、refreshTokenは1day～10yearの設定が可能
  // ※非同期処理
  updateIdToken() {
    const _refreshToken = document.cookie.replace(
      /(?:(?:^|.*;\s*)kumasan_refreshToken\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    )
    const _refreshTokenExpireCookie = document.cookie.replace(
      /(?:(?:^|.*;\s*)refreshToken_expire\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    )
    const _refreshTokenExpire = new Date(_refreshTokenExpireCookie).getTime()
    const nowTime = new Date().getTime()
    if (_refreshTokenExpire < nowTime) {
      alert('ログインしてください')
      location.href = '/login'
    }
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Basic ${process.env.VUE_APP_BASIC_ID}`,
    }
    const params = new URLSearchParams()
    params.append('grant_type', 'refresh_token')
    params.append('refresh_token', _refreshToken)
    axios
      .post(process.env.VUE_APP_COGNITO_URL + '/oauth2/token', params, {
        headers: headers,
      })
      .then(function(data) {
        const _idToken = data.data.id_token
        const _accessToken = data.data.access_token

        document.cookie =
          'kumasan_idToken=' +
          _idToken +
          '; domain=' +
          document.domain +
          '; path=/;'
        document.cookie =
          'kumasan_accessToken=' +
          _accessToken +
          '; domain=' +
          document.domain +
          '; path=/;'

        const _refreshToken = document.cookie.replace(
          /(?:(?:^|.*;\s*)kumasan_refreshToken\s*=\s*([^;]*).*$)|^.*$/,
          '$1'
        )

        const _refreshTokenExpire = new Date()
        _refreshTokenExpire.setTime(
          _refreshTokenExpire.getTime() + 60 * 60 * 1000
        )

        document.cookie =
          'kumasan_refreshToken=' +
          _refreshToken +
          ';domain=' +
          document.domain +
          ';path=/;'

        document.cookie =
          'refreshToken_expire=' +
          _refreshTokenExpire +
          ';domain=' +
          document.domain +
          ';path=/;'
      })
      .catch(function(err) {
        console.error(err)
        Sentry.withScope(scope => {
          scope.setExtra('postData', params)
          Sentry.captureException(err)
        })

        throw err
      })
  }

  // refreshTokenからIdTokenを更新する
  // IdTokenは最大1hの有効期限、refreshTokenは1day～10yearの設定が可能
  // ※同期処理
  async updateAwaitIdToken() {
    const _refreshToken = document.cookie.replace(
      /(?:(?:^|.*;\s*)kumasan_refreshToken\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    )
    const _refreshTokenExpireCookie = document.cookie.replace(
      /(?:(?:^|.*;\s*)refreshToken_expire\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    )
    const _refreshTokenExpire = new Date(_refreshTokenExpireCookie).getTime()
    const nowTime = new Date().getTime()
    if (_refreshTokenExpire < nowTime) {
      alert('ログインしてください')
      location.href = '/login'
    }
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Basic ${process.env.VUE_APP_BASIC_ID}`,
    }
    const params = new URLSearchParams()
    params.append('grant_type', 'refresh_token')
    params.append('refresh_token', _refreshToken)
    await axios
      .post(process.env.VUE_APP_COGNITO_URL + '/oauth2/token', params, {
        headers: headers,
      })
      .then(function(data) {
        const _idToken = data.data.id_token
        const _accessToken = data.data.access_token

        document.cookie =
          'kumasan_idToken=' +
          _idToken +
          '; domain=' +
          document.domain +
          '; path=/;'
        document.cookie =
          'kumasan_accessToken=' +
          _accessToken +
          '; domain=' +
          document.domain +
          '; path=/;'

        const _refreshToken = document.cookie.replace(
          /(?:(?:^|.*;\s*)kumasan_refreshToken\s*=\s*([^;]*).*$)|^.*$/,
          '$1'
        )

        const _refreshTokenExpire = new Date()
        _refreshTokenExpire.setTime(
          _refreshTokenExpire.getTime() + 60 * 60 * 1000
        )

        document.cookie =
          'kumasan_refreshToken=' +
          _refreshToken +
          ';domain=' +
          document.domain +
          ';path=/;'

        document.cookie =
          'refreshToken_expire=' +
          _refreshTokenExpire +
          ';domain=' +
          document.domain +
          ';path=/;'
      })
      .catch(function(err) {
        console.error(err)
        Sentry.withScope(scope => {
          scope.setExtra('postData', params)
          Sentry.captureException(err)
        })

        throw err
      })
  }

  async getLatestIdTokenWithApi() {
    const refreshToken = Cookies.get('kumasan_refreshToken')
    const refreshTokenExpireOfCookie = Cookies.get('refreshToken_expire')

    if (!refreshTokenExpireOfCookie) throw new Error('not refreshToken')

    const refreshTokenExpire = new Date(refreshTokenExpireOfCookie).getTime()

    const nowTime = new Date().getTime()
    if (refreshTokenExpire < nowTime) {
      throw new Error('expired')
    }

    const headers = {
      'Content-Type': CONTENT_TYPE_OF_COGNITO,
      Authorization: AUTHORIZATION_BASIC_ID,
    }

    const params = new URLSearchParams()
    params.append('grant_type', 'refresh_token')
    params.append('refresh_token', refreshToken)

    return await axios
      .post(process.env.VUE_APP_COGNITO_URL + '/oauth2/token', params, {
        headers: headers,
      })
      .then(data => {
        return data.data.id_token
      })
      .catch(err => {
        Sentry.withScope(scope => {
          scope.setExtra('postData', params)
          Sentry.captureException(err)
        })
        throw err
      })
  }

  async getStaffLoginOfficeArrayFromIdToken(idToken: string) {
    const base64Url = idToken.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const decode = JSON.parse(decodeURIComponent(escape(window.atob(base64))))
    const response = await handleUnGzipData(
      decode.data.substring(1, decode.data.length - 1)
    )
    const loginOfficeArray = response

    return loginOfficeArray
  }

  // IdToken取得
  getIdToken() {
    let _res = ''
    _res = document.cookie.replace(
      /(?:(?:^|.*;\s*)kumasan_idToken\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    )
    return _res
  }
  // accessToken取得
  getAccessToken() {
    let _res = ''
    _res = document.cookie.replace(
      /(?:(?:^|.*;\s*)kumasan_accessToken\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    )
    return _res
  }
  // 定期的なトークン確認処理
  checkToken() {
    let _idToken = ''
    _idToken = document.cookie.replace(
      /(?:(?:^|.*;\s*)kumasan_idToken\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    )
    let _refreshToken = ''
    _refreshToken = document.cookie.replace(
      /(?:(?:^|.*;\s*)kumasan_refreshToken\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    )

    // IdToken有効期限切れ（cookieがない）なので、再取得
    if (_idToken == '' && _refreshToken != '') {
      this._updateIdToken
    }
    // トークン（IdTokenとrefreshToken）が存在しない=未認証
    if (_idToken == '' && _refreshToken == '') {
      alert('ログインしてください。')
      document.cookie =
        'kumasan_idToken=; domain=' + document.domain + ';expires=0;path=/;'
      document.cookie =
        'kumasan_refreshToken=; domain=' +
        document.domain +
        ';expires=0;path=/;'
      document.cookie =
        'kumasan_accessToken=; domain=' + document.domain + ';expires=0;path=/;'

      location.replace('/')
    }
  }
  checkTokenTime() {
    const _refreshTokenExpireCookie = document.cookie.replace(
      /(?:(?:^|.*;\s*)refreshToken_expire\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    )
    const _refreshTokenExpire = new Date(_refreshTokenExpireCookie).getTime()
    const nowTime = new Date().getTime()
    if (_refreshTokenExpire < nowTime) {
      alert('ログインしてください')
      location.href = '/login'
    }
  }
  debug() {
    console.log('cookie--------------------')
    console.log(document.cookie)
    console.log('IdToken--------------------')
    console.log(
      document.cookie.replace(
        /(?:(?:^|.*;\s*)kumasan_idToken\s*=\s*([^;]*).*$)|^.*$/,
        '$1'
      )
    )
    if (
      document.cookie.replace(
        /(?:(?:^|.*;\s*)kumasan_idToken\s*=\s*([^;]*).*$)|^.*$/,
        '$1'
      ) == ''
    ) {
      console.log('空です')
    }
    console.log('refreshToken--------------------')
    console.log(
      document.cookie.replace(
        /(?:(?:^|.*;\s*)kumasan_refreshToken\s*=\s*([^;]*).*$)|^.*$/,
        '$1'
      )
    )
  }
}
